import type {
  CreateShowDynamicsRequest,
  ShowDynamics,
  UpdateShowDynamicsRequest,
} from '@musedrops/shared';
import { fromDbKeys, toDbKeys, snakeCase } from '@musedrops/shared';
import { supabase } from '../services/supabase';

export interface ListShowDynamicsOptions {
  page?: number;
  perPage?: number;
  sortField?: keyof ShowDynamics | string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

export async function listShowDynamics(
  options: ListShowDynamicsOptions = {}
): Promise<{ data: ShowDynamics[]; total?: number }> {
  const {
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  let query = supabase.from('show_dynamics').select('*', selectOptions);

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
    });
  } else {
    query = query.order('key', { ascending: true });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch show dynamics: ${error.message}`);
  }

  return {
    data: (data ?? []).map(row =>
      fromDbKeys<ShowDynamics>(row as Record<string, unknown>)
    ),
    total: withTotal ? (count ?? 0) : undefined,
  };
}

export async function getShowDynamics(id: string): Promise<ShowDynamics> {
  const { data, error } = await supabase
    .from('show_dynamics')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch show dynamics: ${error.message}`);
  }

  if (!data) {
    throw new Error('Show dynamics not found');
  }

  return fromDbKeys<ShowDynamics>(data as Record<string, unknown>);
}

export async function createShowDynamics(
  dynamicsData: CreateShowDynamicsRequest
): Promise<ShowDynamics> {
  const { data, error } = await supabase
    .from('show_dynamics')
    .insert(toDbKeys(dynamicsData))
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create show dynamics: ${error.message}`);
  }

  return fromDbKeys<ShowDynamics>(data as Record<string, unknown>);
}

export async function updateShowDynamics(
  id: string,
  dynamicsData: UpdateShowDynamicsRequest
): Promise<ShowDynamics> {
  const { data, error } = await supabase
    .from('show_dynamics')
    .update(toDbKeys(dynamicsData))
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update show dynamics: ${error.message}`);
  }

  return fromDbKeys<ShowDynamics>(data as Record<string, unknown>);
}

export async function deleteShowDynamics(id: string): Promise<void> {
  const { error } = await supabase.from('show_dynamics').delete().eq('id', id);

  if (error) {
    throw new Error(`Failed to delete show dynamics: ${error.message}`);
  }
}

/**
 * Get all show dynamics as a simple array
 */
export async function getAllShowDynamics(): Promise<ShowDynamics[]> {
  const { data } = await listShowDynamics({});
  return data;
}

/**
 * Count shows using a specific dynamics key
 */
export async function countShowsWithDynamics(
  dynamicsKey: string
): Promise<number> {
  const { count, error } = await supabase
    .from('shows')
    .select('id', { count: 'exact', head: true })
    .eq('dynamics_key', dynamicsKey);

  if (error) {
    throw new Error(
      `Failed to count shows with dynamics ${dynamicsKey}: ${error.message}`
    );
  }

  return count ?? 0;
}
